import { FC, useContext } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { useMutation } from '@apollo/client';
import {
  CreateCommentMutation,
  CreateCommentMutationVariables,
  CreateContactMutation,
  CreateContactMutationVariables,
} from '../../generated/types';
import { QueryCreateContact } from '../../graphql/CreateContactQuery';
import { QueryCreateComment } from '../../graphql/CreateCommentQuery';
import { PageContext } from '../../context/PageContext';
import Link from 'next/link';

interface CommentFormProps {
  onSubmit?: () => void;
  parentCommentId?: number;
}

interface CommentFormData {
  name: string;
  username: string;
  email: string;
  comment: string;
  privacy: boolean;
  subject: string;
  _bp: string;
  parentCommentId?: number;
}

export const CommentForm: FC<CommentFormProps> = ({
  onSubmit: onParentSubmit,
  parentCommentId,
}) => {
  const pageContext = useContext(PageContext);
  const toast = useToast();
  const [createComment] = useMutation<
    CreateCommentMutation,
    CreateCommentMutationVariables
  >(QueryCreateComment);

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CommentFormData>();

  const onSubmit = async (values: CommentFormData) => {
    const { data, errors } = await createComment({
      variables: {
        data: {
          ...values,
          entityId: pageContext.entity?.id || 0,
          parentCommentId,
        },
      },
    });

    if (data?.createComment?.created) {
      toast({
        title: 'Kommentar eingereicht.',
        description:
          'Vielen Dank für Deinen Kommentar und Deine Mithilfe. Unsere Redaktion wird den Kommentar in den nächsten Tagen zum Artikel veröffentlichen. Wir sind Trabland.',
        status: 'success',
        duration: 9000,
        isClosable: true,
      });

      reset();
      onParentSubmit && onParentSubmit();
    } else {
      toast({
        title: 'Fehler.',
        description:
          'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl isInvalid={!!errors.name}>
        <FormLabel htmlFor="name">Name (wird nicht veröffentlicht)</FormLabel>
        <Input
          bg="white"
          id="name"
          placeholder="Name"
          {...register('name', {
            required: true,
          })}
        />
        <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mt={4} isInvalid={!!errors.username}>
        <FormLabel htmlFor="username">Veröffentlichter Name</FormLabel>
        <Input
          bg="white"
          id="username"
          placeholder="Veröffentlichter Name"
          {...register('username', {
            required: true,
          })}
        />
        <FormErrorMessage>{errors.username?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mt={4} isInvalid={!!errors.email}>
        <FormLabel htmlFor="email">
          E-Mail (wird nicht veröffentlicht)
        </FormLabel>
        <Input
          bg="white"
          id="email"
          type="email"
          placeholder="E-Mail"
          {...register('email', {
            required: true,
          })}
        />
        <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mt={4} isInvalid={!!errors.comment}>
        <FormLabel htmlFor="comment">Kommentar</FormLabel>
        <Textarea
          bg="white"
          rows={5}
          id="comment"
          {...register('comment', {
            required: true,
          })}
        />
        <FormErrorMessage>{errors.comment?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mt={4} isInvalid={!!errors.privacy}>
        <Checkbox
          mt={4}
          {...register('privacy', {
            required: true,
          })}
        >
          Ich habe die{' '}
          <Link href="/impressum-datenschutz" passHref>
            <Box fontWeight="bold" as="a" textDecoration="underline">
              Datenschutzerklärung
            </Box>
          </Link>{' '}
          gelesen und akzeptiere sie.
        </Checkbox>
        {errors.privacy && (
          <FormErrorMessage>
            Du musst die Datenschutzerklärung akzeptieren
          </FormErrorMessage>
        )}
      </FormControl>

      <Input id="_bp" type="hidden" placeholder="BP" {...register('_bp')} />

      <Flex justifyContent="flex-end" mt={6}>
        <Button
          width="100%"
          variant="roundBlue"
          isLoading={isSubmitting}
          type="submit"
        >
          Kommentar einreichen
        </Button>
      </Flex>
    </form>
  );
};
